import * as React from "react";
import { useParams } from "react-router-dom";

import { Alert, Button, Spinner } from "Components/ui";
import { Lock, RefreshCw, TriangleAlert } from "Components/ui/icons";
import { formatDateTime, formatINR } from "utils/format";
import { usePayPageData, describePublicError } from "./data";
import PayShell from "./Components/PayShell";
import InvoiceSummary from "./Components/InvoiceSummary";
import OtherWaysToPay from "./Components/OtherWaysToPay";

/** Public /pay/:token page: invoice summary and the "Pay securely" checkout button. */
const PayPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { query, invoice, checkout, redirecting, checkoutError } = usePayPageData(token);

  if (query.isLoading) {
    return (
      <PayShell>
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
          <Spinner className="h-5 w-5" />
          Loading your invoice…
        </div>
      </PayShell>
    );
  }

  if (!invoice) {
    return (
      <PayShell>
        <div className="py-8 text-center">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-warning-subtle text-warning-text">
            <TriangleAlert className="h-6 w-6" aria-hidden="true" />
          </span>
          <p className="mt-4 text-base font-semibold text-foreground">
            We couldn't open this invoice
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{describePublicError(query.error)}</p>
          <Button
            variant="secondary"
            leftIcon={<RefreshCw />}
            onClick={() => query.refetch()}
            className="mt-5"
          >
            Try again
          </Button>
        </div>
      </PayShell>
    );
  }

  const busy = checkout.isLoading || redirecting;
  let notice: React.ReactNode = null;
  if (invoice.status === "paid") {
    notice = (
      <Alert tone="success" title="Paid, thank you!">
        {invoice.paidAt ? `Received on ${formatDateTime(invoice.paidAt)}. ` : ""}Nothing more is due
        on this invoice.
      </Alert>
    );
  } else if (invoice.status === "void") {
    notice = <Alert tone="neutral">This invoice was cancelled. Nothing is due.</Alert>;
  } else if (invoice.amountDue <= 0) {
    notice = <Alert tone="neutral">Nothing is due on this invoice.</Alert>;
  } else if (!invoice.payable) {
    notice = (
      <Alert tone="warning">
        Online payment isn't available for this invoice right now. Please contact Meta16 Labs to pay
        by bank transfer, or try again later.
      </Alert>
    );
  }

  const canPay = invoice.payable && invoice.amountDue > 0;

  return (
    <PayShell payee={invoice.payee}>
      <InvoiceSummary invoice={invoice} />
      <div className="mt-6 space-y-3">
        {notice}
        {(canPay || invoice.status === "issued" || invoice.status === "partially_paid") &&
          invoice.amountDue > 0 && (
            <Button
              size="lg"
              fullWidth
              onClick={() => checkout.mutate()}
              disabled={!canPay || busy}
              loading={busy}
              leftIcon={<Lock />}
            >
              {redirecting
                ? "Opening secure checkout…"
                : `Pay ${formatINR(invoice.amountDue)} securely`}
            </Button>
          )}
        {checkoutError && (
          <Alert tone="danger" title="Checkout didn't open">
            {checkoutError}
          </Alert>
        )}
        {canPay && (
          <p className="text-center text-xs text-muted-foreground">
            UPI, cards and net banking. You'll come back here after paying.
          </p>
        )}
        {invoice.amountDue > 0 &&
          (invoice.status === "issued" || invoice.status === "partially_paid") && (
            <OtherWaysToPay payee={invoice.payee} amountLabel={formatINR(invoice.amountDue)} />
          )}
      </div>
    </PayShell>
  );
};

export default PayPage;
